import React, { useState, useEffect } from 'react';
import { Icon } from './icons';
import { Modal } from './sheets';
import { money } from './format';
import {
  goalProgress,
  goalStatus,
  requiredMonthlyContribution,
} from '../lib/forecasting';

const STATUS_META = {
  on_track: { label: 'On track', color: 'var(--ink-3)', quiet: true },
  ahead: { label: 'Ahead', color: 'var(--ink-3)', quiet: true },
  behind: { label: 'Behind', color: 'var(--warn)' },
  overdue: { label: 'Overdue', color: 'var(--alarm)' },
  complete: { label: 'Complete', color: 'var(--ink-3)', quiet: true },
};

function formatTargetDate(dateStr) {
  if (!dateStr) return 'No deadline';
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function GoalEditor({ open, goal, onClose, onSave, onDelete, categories }) {
  const isEdit = !!goal?.id;
  const [name, setName] = useState('');
  const [target, setTarget] = useState('');
  const [startingAmount, setStartingAmount] = useState('');
  const [startDate, setStartDate] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [linkedCategory, setLinkedCategory] = useState('');
  const [type, setType] = useState('savings');
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    if (!open) return;
    const today = new Date().toISOString().slice(0, 10);
    setName(goal?.name || '');
    setTarget(goal?.target ? String(goal.target) : '');
    setStartingAmount(goal?.startingAmount ? String(goal.startingAmount) : '');
    setStartDate(goal?.startDate || today);
    setTargetDate(goal?.targetDate || '');
    setLinkedCategory(goal?.linkedCategory || '');
    setType(goal?.type || 'savings');
  }, [open, goal]);

  const valid = name.trim() && parseFloat(target) > 0;

  const submit = () => {
    try {
      onSave({
        id: goal?.id,
        name,
        target: parseFloat(target),
        startingAmount: parseFloat(startingAmount) || 0,
        startDate,
        targetDate,
        linkedCategory: linkedCategory || null,
        type,
        archived: goal?.archived || false,
      });
      setJustSaved(true);
      setTimeout(() => { setJustSaved(false); onClose(); }, 600);
    } catch (err) {
      console.error('Goal save error:', err);
    }
  };

  const savedCss = justSaved ? 'field input-saved' : 'field';

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? 'Edit goal' : 'New goal'}>
      <div className="sheet-body">
        <div>
          <label className="field-label" htmlFor="goal-name">Goal name</label>
          <input
            id="goal-name"
            className="field"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Vacation fund"
          />
        </div>

        <div className="field-pair">
          <div>
            <label className="field-label" htmlFor="goal-target">Target amount</label>
            <input
              id="goal-target"
              className={savedCss}
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder="5000"
              inputMode="decimal"
            />
          </div>
          <div>
            <label className="field-label" htmlFor="goal-saved">Already saved</label>
            <input
              id="goal-saved"
              className={savedCss}
              value={startingAmount}
              onChange={(e) => setStartingAmount(e.target.value)}
              placeholder="0"
              inputMode="decimal"
            />
          </div>
        </div>

        <div className="field-pair">
          <div>
            <label className="field-label" htmlFor="goal-start">Start date</label>
            <input id="goal-start" className="field" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <label className="field-label" htmlFor="goal-end">Target date</label>
            <input id="goal-end" className="field" type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} />
          </div>
        </div>

        <div>
          {/* The same inverted chip the rail and the filter bars use, rather
              than a third segmented-control style built out of accent fills. */}
          <div className="field-label">Type</div>
          <div role="radiogroup" aria-label="Goal type" style={{ display: 'flex', gap: 6 }}>
            {['savings', 'payoff'].map((t) => (
              <button
                key={t}
                type="button"
                role="radio"
                aria-checked={type === t}
                onClick={() => setType(t)}
                className={`chip pressable ${type === t ? 'is-active' : ''}`}
                style={{ textTransform: 'capitalize' }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="field-label" htmlFor="goal-cat">Linked category (optional)</label>
          <select id="goal-cat" className="field" value={linkedCategory} onChange={(e) => setLinkedCategory(e.target.value)}>
            <option value="">No category — track manually</option>
            {categories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <div className="field-note">
            Income transactions in this category since the start date count toward progress.
          </div>
        </div>

        <button onClick={submit} className="btn-primary is-block pressable" disabled={!valid}>
          {isEdit ? 'Save changes' : 'Create goal'}
        </button>
        {isEdit && (
          <button
            onClick={() => {
              if (window.confirm(`Delete goal "${goal.name}"?`)) {
                onDelete(goal.id);
                onClose();
              }
            }}
            className="btn-danger is-block pressable"
          >
            Delete goal
          </button>
        )}
      </div>
    </Modal>
  );
}

function GoalRow({ goal, transactions, onPick }) {
  const today = new Date();
  const progress = goalProgress(goal, transactions);
  const status = goalStatus(goal, transactions, today);
  const required = requiredMonthlyContribution(goal, transactions, today);
  const meta = STATUS_META[status];

  // Budget dropped its bars because "how much is left" is a number, not a
  // proportion. A goal genuinely is a proportion, so the bar stays — but in
  // plain ink, carrying progress rather than doubling as a status colour.
  return (
    <button onClick={() => onPick(goal)} className="row-line pressable" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <div style={{ fontSize: 14.5, fontWeight: 500, color: 'var(--ink)' }}>{goal.name}</div>
        <div className="num" style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--ink)' }}>
          {money(progress.contributed, { cents: false })} <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>/ {money(goal.target, { cents: false })}</span>
        </div>
      </div>
      <div style={{ height: 4, background: 'var(--surface-3)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{
          width: `${progress.percentComplete}%`,
          height: '100%',
          background: meta.quiet ? 'var(--ink)' : meta.color,
          transition: 'width 0.4s ease',
        }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12, color: 'var(--ink-3)' }}>
        <span style={{ color: meta.color, fontWeight: meta.quiet ? 400 : 600 }}>{meta.label}</span>
        <span>
          {status === 'complete' ? 'Funded' : Number.isFinite(required) ? <>{money(required, { cents: false })}/mo to {formatTargetDate(goal.targetDate)}</> : 'Past deadline'}
        </span>
      </div>
    </button>
  );
}

export function GoalsView({ goals, transactions, categories, onSave, onDelete, onArchive, desktop }) {
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const active = goals.filter((g) => !g.archived);
  const archived = goals.filter((g) => g.archived);
  const totalActive = active.length;
  const totalTarget = active.reduce((s, g) => s + (Number(g.target) || 0), 0);
  const totalContributed = active.reduce((s, g) => s + goalProgress(g, transactions).contributed, 0);
  const totalMonthly = active.reduce((s, g) => {
    const need = requiredMonthlyContribution(g, transactions);
    return s + (Number.isFinite(need) ? need : 0);
  }, 0);

  const openNew = () => { setEditingGoal(null); setEditorOpen(true); };
  const openEdit = (g) => { setEditingGoal(g); setEditorOpen(true); };

  return (
    <div className="pg" style={{ maxWidth: desktop ? 760 : 'none', width: '100%' }}>
      {/* The lead figure is what you have put away; the target is its caption.
          Same shape as Today and Budget rather than a fourth card style. */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 18, flexWrap: 'wrap' }}>
        {totalActive === 0 ? (
          <p className="pg-note" style={{ fontSize: 14, color: 'var(--ink-2)' }}>
            Set a goal to start tracking a savings target or a payoff.
          </p>
        ) : (
          <>
            <div className="pg-figure">{money(totalContributed, { cents: false })}</div>
            <div className="pg-lede">
              of <b style={{ color: 'var(--ink)', fontWeight: 600 }}>{money(totalTarget, { cents: false })}</b> across{' '}
              {totalActive} active {totalActive === 1 ? 'goal' : 'goals'}
              {totalMonthly > 0 && <>, needing <b style={{ color: 'var(--ink)', fontWeight: 600 }}>{money(totalMonthly, { cents: false })}</b>/mo to stay on track</>}
            </div>
          </>
        )}
        <div style={{ flexGrow: 1 }} />
        <button onClick={openNew} className="btn-primary pressable" style={{ marginBottom: 6 }}>
          <Icon name="plus" size={16} sw={2.4} /> New goal
        </button>
      </div>

      {active.length === 0 ? (
        <p className="pg-note">No goals yet — set one to track savings or a payoff.</p>
      ) : (
        <div className="pg-section">
          <div className="eyebrow">Active</div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {active.map((g) => (
              <GoalRow key={g.id} goal={g} transactions={transactions} onPick={openEdit} />
            ))}
          </div>
        </div>
      )}

      {archived.length > 0 && (
        <div className="pg-section">
          <button
            onClick={() => setShowArchived((s) => !s)}
            className="pressable"
            style={{
              width: '100%', textAlign: 'left', background: 'transparent', border: 'none',
              fontSize: 13, color: 'var(--ink-3)', fontWeight: 600,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}
          >
            <span>Archived ({archived.length})</span>
            <Icon name="chevD" size={16} />
          </button>
          {showArchived && archived.map((g) => (
            <div key={g.id} style={{ borderTop: '1px solid var(--line)', padding: '12px 0', display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
              <span style={{ color: 'var(--ink-2)' }}>{g.name}</span>
              <button
                onClick={() => onArchive(g.id, false)}
                className="pressable"
                style={{ background: 'transparent', border: 'none', color: 'var(--accent)', fontWeight: 600, fontSize: 13 }}
              >
                Restore
              </button>
            </div>
          ))}
        </div>
      )}

      <GoalEditor
        open={editorOpen}
        goal={editingGoal}
        onClose={() => setEditorOpen(false)}
        onSave={onSave}
        onDelete={onDelete}
        categories={categories}
      />
    </div>
  );
}
